import {
  AddMerchantPayload,
  AddMerchantResponse,
  addNewMerchant,
  blockMerchant as blockMerchantApi,
  getMerchants,
  getMerchantSettlement,
  batchBalanceByAddress,
  MerchantApiRecord,
  MerchantSettlementInfo,
  SignUpMerchantPayload,
  PrepareMerchantSignupPayload,
  PrepareMerchantSignupResponse,
  prepareMerchantSignup as prepareMerchantSignupApi,
  signupNewMerchant,
  lookupMerchantApi,
  SpecificMerchant,
  checkMerchantTokenApprovals,
  grantMerchantTokenAproval,
  suspendMerchantApi,
  activateMerchantApi,
} from "utils/AxiosUtil";
import type { NetworkType } from "utils/network";
import type { ApprovalAudit, GrantResult } from "types/approvals";

async function addMerchant(
  payload: AddMerchantPayload,
  network: NetworkType,
): Promise<AddMerchantResponse> {
  const res = await addNewMerchant(payload, network);
  return res.data;
}

async function signUpMerchant(payload: SignUpMerchantPayload) {
  const res = await signupNewMerchant(payload);
  return res.data;
}

// The signup preflight. Read-only: an existing merchant account handed a new merchant has it
// attached here.
async function prepareSignup(
  payload: PrepareMerchantSignupPayload,
): Promise<PrepareMerchantSignupResponse> {
  const res = await prepareMerchantSignupApi(payload);
  return res.data;
}

async function suspendMerchant(merchantId: string, network: NetworkType) {
  const res = await suspendMerchantApi(merchantId, network);
  return res.data;
}

async function activateMerchant(merchantId: string, network: NetworkType) {
  const res = await activateMerchantApi(merchantId, network);
  return res.data;
}

async function blockMerchant(merchantId: string, network: NetworkType) {
  const res = await blockMerchantApi(merchantId, network);
  return res.data;
}

// An unknown id isn't an error on the chain: `merchantAccount` returns a zeroed struct, which
// the backend passes through as a 200 with merchantStatus UNASSIGNED (0). That is the definitive
// miss and resolves to `null`; real failures still throw, so a dead network is never reported as
// "no such merchant".
async function lookupMerchant(
  merchantId: string,
  network: NetworkType,
  token?: string,
): Promise<SpecificMerchant | null> {
  const res = await lookupMerchantApi(merchantId, network, token);
  const merchant = res.data as SpecificMerchant | undefined;
  return merchant?.merchantStatus ? merchant : null;
}

async function fetchMerchants(
  treasuryId: string,
  network: NetworkType,
): Promise<MerchantApiRecord[]> {
  return getMerchants(treasuryId, network);
}

async function fetchBalancesByAddresses(
  addresses: string[],
  network: NetworkType,
  token?: string,
): Promise<Record<string, number>> {
  return batchBalanceByAddress(addresses, network, token);
}

async function fetchMerchantSettlement(
  merchantId: string,
): Promise<MerchantSettlementInfo> {
  return getMerchantSettlement(merchantId);
}

// Read-only: reports which supported tokens the merchant address is missing an allowance for.
async function checkTokenApprovals(
  merchantId: string,
  network: NetworkType,
): Promise<ApprovalAudit> {
  const res = await checkMerchantTokenApprovals(merchantId, network);
  return res.data;
}

// Sends transactions — a POL top-up, then an `approve` signed by the merchant's own wallet. One
// token, named explicitly: the backend refuses an omitted one rather than guessing.
async function grantTokenApproval(
  merchantId: string,
  token: string,
  network: NetworkType,
): Promise<GrantResult> {
  const res = await grantMerchantTokenAproval(merchantId, token, network);
  return res.data;
}

const merchant_services = {
  addMerchant,
  signUpMerchant,
  prepareSignup,
  blockMerchant,
  suspendMerchant,
  activateMerchant,
  lookupMerchant,
  fetchMerchants,
  fetchBalancesByAddresses,
  fetchMerchantSettlement,
  checkTokenApprovals,
  grantTokenApproval,
};
export default merchant_services;
